import { UploadStatus } from "@/shared/enums/upload-status";
import { createSlice } from "@reduxjs/toolkit";

export interface Artifact {
	artifactId: string;
	datasetId: string;
	fileName: string;
	status: UploadStatus;
	streamingId?: string;
}

export interface UploadProgressItem {
	id: string;
	fileName: string;
	status: UploadStatus;
	datasetId: string;
	hideFromToast?: boolean;
}

export interface ArtifactProcess {
	artifactsProcessed: Array<Artifact>;
	activeUploads: Array<UploadProgressItem>;
}

// Incializar el estado en REDUX TOOKIT (RTK)
const initialState: ArtifactProcess = {
	artifactsProcessed: new Array<Artifact>(),
	activeUploads: new Array<UploadProgressItem>(),
};

// Operaciones que modifican el estado en REDUX TOOKIT (RTK)
const artifactUploaderSlice = createSlice({
	name: "artifactId",
	initialState,
	reducers: {
		artifactProcessed: (state, action) => {
			state.artifactsProcessed.push({
				artifactId: action.payload.id,
				datasetId: action.payload.datasetId,
				fileName: action.payload.fileName,
				status: action.payload.status,
				streamingId: action.payload.streamingId,
			});

			// Actualizar o agregar a activeUploads
			const existingUploadIndex = state.activeUploads.findIndex(
				(upload) =>
					upload.id === action.payload.fileName &&
					upload.datasetId === action.payload.datasetId,
			);

			if (existingUploadIndex >= 0) {
				state.activeUploads[existingUploadIndex].status = action.payload.status;
			} else {
				state.activeUploads.push({
					id: action.payload.fileName,
					fileName: action.payload.fileName,
					status: action.payload.status,
					datasetId: action.payload.datasetId,
				});
			}
		},
		uploadStarted: (state, action) => {
			const existingUploadIndex = state.activeUploads.findIndex(
				(upload) =>
					upload.id === action.payload.id &&
					upload.datasetId === action.payload.datasetId,
			);

			if (existingUploadIndex >= 0) {
				state.activeUploads[existingUploadIndex] = action.payload;
			} else {
				state.activeUploads.push(action.payload);
			}
		},
		removeUpload: (state, action) => {
			state.activeUploads = state.activeUploads.filter(
				(upload) =>
					!(
						upload.id === action.payload.id &&
						upload.datasetId === action.payload.datasetId
					),
			);
		},
		clearCompletedUploads: (state) => {
			state.activeUploads = state.activeUploads.filter(
				(upload) => upload.status !== UploadStatus.COMPLETED,
			);
		},
		clearErrorUploads: (state) => {
			state.activeUploads = state.activeUploads.filter(
				(upload) =>
					upload.status !== UploadStatus.UPLOADING_ERROR &&
					upload.status !== UploadStatus.ANALYZING_ERROR,
			);
		},
		clearAllFinishedUploads: (state) => {
			state.activeUploads = state.activeUploads.filter(
				(upload) =>
					upload.status !== UploadStatus.COMPLETED &&
					upload.status !== UploadStatus.UPLOADING_ERROR &&
					upload.status !== UploadStatus.ANALYZING_ERROR,
			);
		},
		clearAllUploads: (state) => {
			state.activeUploads = [];
			state.artifactsProcessed = [];
		},
		hideUploadFromToast: (state, action) => {
			const uploadIndex = state.activeUploads.findIndex(
				(upload) =>
					upload.id === action.payload.id &&
					upload.datasetId === action.payload.datasetId,
			);
			if (uploadIndex >= 0) {
				state.activeUploads[uploadIndex].hideFromToast = true;
			}
		},
		hideAllUploadsFromToast: (state) => {
			for (const upload of state.activeUploads) {
				upload.hideFromToast = true;
			}
		},
	},
});

export default artifactUploaderSlice;
